import { ApiService } from '../ApiService';
import { devTestSettings } from '../devTestSettings';
import { plainToClass } from '~/models/transform';
import { GiftCertificatesView, SubscribeInfo, TransactionHistoryView } from '~/models/Billing';
import { BaseListRequestParams } from '~/models/BaseRequestParams';

export interface IApplePurchaseRequest {
  purchaseToken?: string;
}

export interface IGooglePlayPurchaseRequest {
  orderId: string | null | undefined;
  packageName: string | null | undefined;
  productId: string | null | undefined;
  purchaseToken: string | null | undefined;
}

export interface IBillingResponse {
  Code:
    | 'SUCCESS'
    | 'SUCCESS_BOGO_APPLIED'
    | 'SUCCESS_CLAIMED'
    | 'IGNORED_DUPLICATE'
    | 'IGNORED_NOT_A_PURCHASE'
    | 'IGNORED_CROSSGRADE';
}

export interface IRedeemGiftCertificateResponse {
  Code: 'SUCCESS' | 'INVALID_FORMAT' | 'TOO_MANY_ATTEMPTS' | 'NOT_FOUND' | 'ALREADY_REDEEMED';
  Days?: number;
  PremiumEndsOnUtc?: string;
}

// Dev/QA test harness: when the "Force billing failure" switch is on (Settings -> Developer
// Tools), the purchase-validation calls throw AFTER the store payment has completed, leaving
// the purchase owned-but-unacknowledged so the reconciliation/retry path can be exercised.
// The flag is in-memory (resets on app start) and only settable from the dev-gated Settings
// section, so production is unaffected.
const failIfForced = (endpoint: string): void => {
  if (devTestSettings.forceBillingFailure) {
    throw new Error(`Forced ${endpoint} failure (dev test harness)`);
  }
};

export class BillingService {
  /**
   * Submit an Apple (App Store) purchase receipt for validation and processing.
   * @param purchase Apple purchase payload (supports expo-iap fields)
   */
  static async processApplePurchase(purchase: IApplePurchaseRequest): Promise<IBillingResponse> {
    failIfForced('billing/apple');
    return await ApiService.post<IBillingResponse>('billing/apple', purchase);
  }

  /**
   * Submit a Google Play purchase token for validation and processing.
   * @param payload Google Play purchase payload (supports expo-iap fields)
   */
  static async processGooglePurchase(payload: IGooglePlayPurchaseRequest): Promise<IBillingResponse> {
    failIfForced('billing/google');
    return await ApiService.post<IBillingResponse>('billing/google', payload);
  }

  /**
   * Fetch paginated transaction history for the current member.
   * @param params Optional pagination params: { skip, take }
   */
  static async getTransactionHistory(params?: BaseListRequestParams): Promise<TransactionHistoryView> {
    const response = await ApiService.get<TransactionHistoryView>('billing/transactions', params);
    return plainToClass(TransactionHistoryView, response);
  }

  /**
   * Fetch paginated gift certificates of the current member: those issued to them and those they redeemed.
   * @param params Optional pagination params: { skip, take }
   */
  static async getGiftCertificates(params?: BaseListRequestParams): Promise<GiftCertificatesView> {
    const response = await ApiService.get<GiftCertificatesView>('billing/certificates', params);
    return plainToClass(GiftCertificatesView, response);
  }

  /**
   * Redeem a gift certificate code, adding its premium days to the current member.
   * @param code Gift certificate code, with or without dashes
   */
  static async redeemGiftCertificate(code: string): Promise<IRedeemGiftCertificateResponse> {
    return await ApiService.post<IRedeemGiftCertificateResponse>('billing/certificates/redeem', { Code: code });
  }

  /**
   * Fetch subscription information for the current member.
   */
  static async getSubscribeInfo(): Promise<SubscribeInfo> {
    const response = await ApiService.get<SubscribeInfo>('billing/subscribe-info');
    return plainToClass(SubscribeInfo, response);
  }
}
